import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, DollarSign, TrendingUp, Users, Percent } from "lucide-react";
import api from "../lib/api";

const fmtUSD = (n) =>
  `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtUSD2 = (n) =>
  `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;

/**
 * Compact platform-revenue summary card. Mounted at the top of the Super Admin
 * Overview page to give immediate visibility into MRR / ARR / ARPU / Active
 * Subs. Links out to the full Platform Revenue page for drill-down.
 *
 * Admin-only — caller is responsible for the role gate.
 */
export default function AdminRevenueSummary() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/admin/revenue")
      .then((r) => setData(r.data))
      .catch(() => setData(false));
  }, []);

  if (data === null) {
    return (
      <div
        className="mb-8 bg-white border border-slate-200 rounded-sm p-5 animate-pulse"
        data-testid="admin-revenue-summary-loading"
      >
        <div className="h-4 bg-slate-200 rounded w-1/3 mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-slate-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (data === false) {
    return null; // permission error or backend down — silent
  }

  const stats = [
    {
      label: "MRR",
      value: fmtUSD(data.mrr),
      icon: DollarSign,
      tone: "text-emerald-700",
      testid: "admin-rev-mrr",
    },
    {
      label: "ARR",
      value: fmtUSD(data.arr),
      icon: TrendingUp,
      tone: "text-slate-900",
      testid: "admin-rev-arr",
    },
    {
      label: "ARPU",
      value: fmtUSD2(data.arpu),
      icon: DollarSign,
      tone: "text-slate-900",
      testid: "admin-rev-arpu",
    },
    {
      label: "Active subs",
      value: `${data.active_subscriptions || 0} / ${data.total_landlords || 0}`,
      icon: Users,
      tone: "text-slate-900",
      testid: "admin-rev-active",
    },
  ];

  return (
    <div
      className="mb-8 bg-white border border-slate-200 rounded-sm p-5"
      data-testid="admin-revenue-summary"
    >
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="overline text-slate-500">Platform revenue</div>
          <h2
            className="text-lg font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "Outfit" }}
          >
            Revenue dashboard
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Snapshot of recurring revenue, paying customers, and churn across the
            entire platform.
          </p>
        </div>
        <Link
          to="/dashboard/admin/revenue"
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900 border border-slate-300 hover:border-slate-900 rounded-sm px-3 py-1.5 transition-colors shrink-0"
          data-testid="admin-revenue-summary-cta"
        >
          Open full dashboard <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div
            key={s.label}
            className="border border-slate-100 rounded-sm p-3"
            data-testid={s.testid}
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              <s.icon className="w-3 h-3" />
              {s.label}
            </div>
            <div className={`mt-2 text-xl font-mono font-bold ${s.tone}`}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {data.churn_rate !== undefined && (
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
          <Percent className="w-3 h-3" />
          <span>
            Churn this month:{" "}
            <span
              className={`font-semibold ${
                data.churn_rate > 5 ? "text-red-700" : "text-slate-900"
              }`}
              data-testid="admin-rev-churn"
            >
              {(data.churn_rate || 0).toFixed(1)}%
            </span>{" "}
            ({data.churned_this_month || 0} cancellations)
          </span>
        </div>
      )}
    </div>
  );
}
